import { useBundle } from '@amzn/react-arb-tools';
import React, { useEffect, useState } from 'react';
import ErrorIllustration from 'src/assets/su-error-illustration.png';
import { ROUTES } from 'src/constants/route';
import { REDIRECT_DELAY_SECONDS } from 'src/constants/timing';
import { t } from 'src/utils/translation';
import { ERROR_ILLUSTRATION_ALT } from 'src/constants/entity';

export interface ErrorViewProps extends React.ComponentProps<'div'> {
  errorHeader?: string;
  errorMessage?: string;
  statusCode?: number;
}

/**
 * Standardized error view with auto-redirect for 4XX errors.
 *
 * **4XX errors always show countdown and auto-redirect**
 * Custom errorHeader/errorMessage will replace default text but countdown always appears.
 *
 * @param {string} [errorHeader] - Custom header (replaces default header)
 * @param {string} [errorMessage] - Custom message (replaces default description, countdown still shown)
 * @param {number} [statusCode] - HTTP status code (4XX = client error with redirect, other = generic error)
 * @param {string} [className] - Additional CSS classes
 */

const ErrorView = ({
  errorHeader,
  errorMessage,
  statusCode,
  className,
  ...rest
}: ErrorViewProps) => {
  const [coreBundle] = useBundle('core');
  const [countdown, setCountdown] = useState(REDIRECT_DELAY_SECONDS);

  // Check if this is a client error (4XX)
  const isClientError = statusCode !== undefined && statusCode >= 400 && statusCode < 500;

  // Auto-redirect for 4XX errors - always runs regardless of custom messages
  useEffect(() => {
    if (isClientError) {
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            window.location.replace(ROUTES.NEW_LEARN.BASE);
            return prev;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(countdownInterval);
      };
    }
    return undefined;
  }, [isClientError]);

  // Determine error content based on status code
  let finalErrorHeader: string;
  let finalErrorMessage: React.ReactNode;

  if (isClientError) {
    // 4XX client error - always show countdown
    finalErrorHeader = errorHeader || t(coreBundle, 'client_error_header');
    const description = errorMessage || t(coreBundle, 'client_error_description');

    finalErrorMessage = (
      <>
        <p>{description}</p>
        <p>
          {t(coreBundle, 'redirect_message')}
          {' '}
          {countdown}
        </p>
      </>
    );
  } else {
    // Everything else (5XX, no status, invalid status) - generic error message
    finalErrorHeader = errorHeader || t(coreBundle, 'generic_error_header');
    finalErrorMessage = errorMessage || t(coreBundle, 'generic_error_description', { number: 3 });
  }

  return (
    <div
      className={`m-auto flex w-full max-w-[30rem] flex-col items-center gap-su-space-05 ${className || ''}`}
      {...rest}
    >
      <img src={ErrorIllustration} className="w-full max-w-[22rem]" alt={ERROR_ILLUSTRATION_ALT} />
      <div className="whitespace-pre-line text-pretty text-center">
        {/* Using plain div w/ custom aria labels to ensure Katal won't override font styling for Header tags */}
        <div role="heading" aria-level={2} className="mb-su-space-03 text-su-heading-600 font-bold leading-6">
          {finalErrorHeader}
        </div>
        <div className="font-normal leading-normal text-su-text-secondary">{finalErrorMessage}</div>
      </div>
    </div>
  );
};

export default ErrorView;
